import * as React from "react";

export type NoteAutosaveStatus = { savedAt: string } | { error: string } | null;

type NoteVersion = string | null | undefined;

export interface NoteMissingField {
    element: HTMLElement;
    label: string;
}

interface NoteEditContextValue {
    autosaveStatus: NoteAutosaveStatus;
    setAutosaveStatus: (status: NoteAutosaveStatus) => void;
    hasEditConflict: boolean;
    setHasEditConflict: (hasEditConflict: boolean) => void;
    /** The `note_last_modified_at` this tab last loaded or saved */
    versionRef: React.MutableRefObject<NoteVersion> | null;
    /** Runs note saves one at a time so each one sends the latest version */
    runExclusive: <T>(task: () => Promise<T>) => Promise<T>;
    /** Fields that block signing, in page order */
    missingFields: NoteMissingField[];
    setMissingFields: (missingFields: NoteMissingField[]) => void;
    /** Set when the submit button is clicked, consumed by the next submit */
    signAttemptRef: React.MutableRefObject<boolean> | null;
    /** Re-applies signing validation after a draft-rules validation */
    restoreSigningValidationRef: React.MutableRefObject<
        (() => Promise<void>) | null
    > | null;
}

const NoteEditContext = React.createContext<NoteEditContextValue>({
    autosaveStatus: null,
    setAutosaveStatus: () => undefined,
    hasEditConflict: false,
    setHasEditConflict: () => undefined,
    versionRef: null,
    runExclusive: (task) => task(),
    missingFields: [],
    setMissingFields: () => undefined,
    signAttemptRef: null,
    restoreSigningValidationRef: null,
});

export const NOTE_EDIT_CONFLICT_MESSAGE =
    "This note was changed in another tab or device. Copy any unsaved text you need, then reload to get the latest version.";

export const isNewerNoteVersion = (
    remoteVersion: NoteVersion,
    localVersion: NoteVersion
) =>
    Boolean(
        remoteVersion &&
            localVersion &&
            Date.parse(remoteVersion) > Date.parse(localVersion)
    );

export function NoteEditProvider({ children }: React.PropsWithChildren) {
    const [autosaveStatus, setAutosaveStatus] =
        React.useState<NoteAutosaveStatus>(null);
    const [hasEditConflict, setHasEditConflict] = React.useState(false);
    const [missingFields, setMissingFieldsState] = React.useState<
        NoteMissingField[]
    >([]);
    const setMissingFields = React.useCallback(
        (nextMissingFields: NoteMissingField[]) =>
            setMissingFieldsState((previousMissingFields) =>
                previousMissingFields.length === 0 &&
                nextMissingFields.length === 0
                    ? previousMissingFields
                    : nextMissingFields
            ),
        []
    );
    const signAttemptRef = React.useRef(false);
    const restoreSigningValidationRef = React.useRef<
        (() => Promise<void>) | null
    >(null);
    const versionRef = React.useRef<NoteVersion>(undefined);
    const queueRef = React.useRef<Promise<unknown>>(Promise.resolve());
    const runExclusive = React.useCallback(<T,>(task: () => Promise<T>) => {
        const result = queueRef.current.catch(() => undefined).then(task);
        queueRef.current = result;
        return result;
    }, []);
    const value = React.useMemo(
        () => ({
            autosaveStatus,
            setAutosaveStatus,
            hasEditConflict,
            setHasEditConflict,
            versionRef,
            runExclusive,
            missingFields,
            setMissingFields,
            signAttemptRef,
            restoreSigningValidationRef,
        }),
        [
            autosaveStatus,
            hasEditConflict,
            runExclusive,
            missingFields,
            setMissingFields,
        ]
    );

    return (
        <NoteEditContext.Provider value={value}>
            {children}
        </NoteEditContext.Provider>
    );
}

export const useNoteEditContext = () => React.useContext(NoteEditContext);
